import { Firestore, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import type { Comment } from '@common/schemas/comment.schema';
import { CommentSchema } from '@common/schemas/comment.schema';
import type { Novel, NovelStorageLayout } from '@/lib/types';
import type { CommentStore } from '../contracts/comments';
import { normalizeDates } from '@/lib/date-utils';
import { v4 as uuidv4 } from 'uuid';

export class FirebaseCommentStore implements CommentStore {
  constructor(private firestore: Firestore) {}

  subscribeComments(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    textId: string,
    onUpdate: (comments: Comment[]) => void,
    onError: (error: Error) => void
  ): () => void {
    const resolver = new StorageResolver(this.firestore);
    const { ref } = resolver.resolveItem(ownerId, novelId, 'narrative', 'contents', textId, layout);

    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          onUpdate([]);
          return;
        }

        const data = snap.data();
        const rawCommentsMap = data?.comments || {};

        const docs = Object.entries(rawCommentsMap).map(([id, commentData]: [string, any]) => {
          const rawComment = normalizeDates({
            ...commentData,
            id,
          });

          try {
            return CommentSchema.parse(rawComment);
          } catch (err) {
            console.error("Comment validation error:", err, rawComment);
            return rawComment as Comment;
          }
        });

        // Sort by creation date
        docs.sort((a, b) => {
          const dateA = new Date(a.audit.createdAt).getTime() || 0;
          const dateB = new Date(b.audit.createdAt).getTime() || 0;
          return dateA - dateB;
        });

        onUpdate(docs);
      },
      onError
    );
  }

  async addComment(
    ownerId: string,
    novel: Novel,
    textId: string,
    authorId: string,
    authorName: string,
    content: string
  ): Promise<string | null> {
    const resolver = new StorageResolver(this.firestore);
    const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'contents', textId, novel.storageLayout);

    const id = uuidv4();
    const now = new Date().toISOString();
    const newCommentData = {
      authorId,
      authorName,
      content,
      status: 'open',
      audit: {
        createdAt: now,
        updatedAt: now,
      }
    };

    const updates = {
      [`comments.${id}`]: newCommentData,
    };
    await updateDoc(ref, updates);

    return id;
  }

  async resolveComment(
    ownerId: string,
    novel: Novel,
    textId: string,
    commentId: string
  ): Promise<void> {
    const resolver = new StorageResolver(this.firestore);
    const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'contents', textId, novel.storageLayout);

    const updates = {
      [`comments.${commentId}.status`]: 'resolved',
      [`comments.${commentId}.audit.updatedAt`]: new Date().toISOString()
    };
    await updateDoc(ref, updates);
  }

  async deleteComment(
    ownerId: string,
    novel: Novel,
    textId: string,
    commentId: string
  ): Promise<void> {
    const resolver = new StorageResolver(this.firestore);
    const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'contents', textId, novel.storageLayout);

    const updates = {
      [`comments.${commentId}`]: deleteField()
    };
    await updateDoc(ref, updates);
  }

  async addReply(
    ownerId: string,
    novel: Novel,
    textId: string,
    commentId: string,
    authorId: string,
    authorName: string,
    content: string,
    existingReplies: Comment['replies']
  ): Promise<void> {
    const resolver = new StorageResolver(this.firestore);
    const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'contents', textId, novel.storageLayout);

    const replyId = uuidv4();
    const now = new Date().toISOString();
    const newReply = {
      id: replyId,
      authorId,
      authorName,
      content,
      audit: {
        createdAt: now,
        updatedAt: now,
      },
    };

    const updatedReplies = [...(existingReplies || []), newReply];

    const updates = {
      [`comments.${commentId}.replies`]: updatedReplies,
      [`comments.${commentId}.audit.updatedAt`]: now,
    };
    await updateDoc(ref, updates);
  }
}
